type GtagFn = (...args: unknown[]) => void

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: GtagFn
    __legalPrimeGoogleLast?: { measurementKey: string; at: number }
    __legalPrimeGtmContainers?: Record<string, true>
    google_tag_manager?: unknown
  }
}

const GTAG_JS_BASE = 'https://www.googletagmanager.com/gtag/js'
const GTM_LOADER_SCRIPT_ID = 'legalprime-gtm-bootstrap'
const GTAG_LOADER_SCRIPT_PREFIX = 'legalprime-gtag-js-'

const initializedGa4Measurements = new Set<string>()

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null

  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))

  return match ? decodeURIComponent(match[1]) : null
}

function normalizeGa4MeasurementId(value: string | null | undefined): string | null {
  const v = (value ?? '').trim().toUpperCase()
  if (!v) return null
  return /^G-[A-Z0-9]+$/.test(v) ? v : null
}

function normalizeGtmContainerId(value: string | null | undefined): string | null {
  const v = (value ?? '').trim().toUpperCase()
  if (!v) return null
  return /^GTM-[A-Z0-9_-]+$/.test(v) ? v : null
}

function ensureDataLayer(): unknown[] {
  if (typeof window === 'undefined') return []
  window.dataLayer = window.dataLayer ?? []
  return window.dataLayer as unknown[]
}

function ensureGtag(): GtagFn {
  ensureDataLayer()
  const gtag: GtagFn = function (...args: unknown[]) {
    window.dataLayer?.push(args)
  }
  window.gtag = gtag
  return gtag
}

function loadExternalScript(params: {
  src: string
  id: string
  async?: boolean
  onLoad?: () => void
}): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(params.id)) {
    params.onLoad?.()
    return
  }
  const script = document.createElement('script')
  script.id = params.id
  script.src = params.src
  script.async = params.async ?? true
  if (params.onLoad) {
    script.onload = params.onLoad
  }
  document.head.appendChild(script)
}

/** Extrai client_id compatível com GA4/GTAG a partir do cookie _ga, quando existir */
export function extractGaCookieClientId(): string | null {
  const raw = readCookie('_ga')
  if (!raw) return null
  const parts = raw.split('.')
  if (parts.length < 4) return null
  const segmentA = parts[2]
  const segmentB = parts[3]
  if (/^[A-Za-z0-9]+$/.test(segmentA) && /^[A-Za-z0-9]+$/.test(segmentB)) {
    return `${segmentA}.${segmentB}`
  }
  return null
}

/**
 * Tentativa conservadora de session_id numérico a partir dos cookies típicos do GA4 quando presentes.
 */
export function extractGaSessionIdGuess(): string | null {
  if (typeof document === 'undefined') return null

  const cookies = document.cookie.split(';')
  for (const part of cookies) {
    const [name, val] = part.split('=').map((s) => s.trim())
    if (!name || !val) continue
    if (/^_ga_[A-Z0-9]+$/.test(name)) {
      const decoded = decodeURIComponent(val)
      const parts = decoded.split('.')
      if (parts.length >= 3 && /^\d+$/.test(parts[2])) {
        return parts[2]
      }
    }
  }
  return null
}

export function ensureGoogleTrackingBrowserFields(): Record<string, string> {
  const out: Record<string, string> = {}
  const clientId = extractGaCookieClientId()
  const sessionGuess = extractGaSessionIdGuess()
  if (clientId) out.ga_client_id = clientId
  if (sessionGuess) out.ga_session_id = sessionGuess

  return out
}

function shouldTrackMeasurementPageView(measurementId: string): boolean {
  if (typeof window === 'undefined') return false

  const key = measurementId
  const now = Date.now()
  const last = window.__legalPrimeGoogleLast

  if (last && last.measurementKey === key && now - last.at < 1000) {
    return false
  }

  window.__legalPrimeGoogleLast = { measurementKey: key, at: now }
  return true
}

export function bootstrapGtmContainer(containerId: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const id = normalizeGtmContainerId(containerId)
  if (!id) return

  window.__legalPrimeGtmContainers = window.__legalPrimeGtmContainers ?? {}
  if (window.__legalPrimeGtmContainers[id]) return

  ensureDataLayer()
  window.__legalPrimeGtmContainers[id] = true

  window.dataLayer?.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`
  script.id = `${GTM_LOADER_SCRIPT_ID}-${id.replace(/[^A-Za-z0-9_-]/g, '')}`
  document.head.appendChild(script)
}

function bootstrapGtagMeasurement(measurementId: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const id = normalizeGa4MeasurementId(measurementId)
  if (!id) return

  const gtag = ensureGtag()

  loadExternalScript({
    src: `${GTAG_JS_BASE}?id=${encodeURIComponent(id)}`,
    id: `${GTAG_LOADER_SCRIPT_PREFIX}${id}`,
    async: true,
    onLoad: () => {
      if (!initializedGa4Measurements.has(id)) {
        gtag('js', new Date())
        gtag('config', id)
        initializedGa4Measurements.add(id)
      }
      if (shouldTrackMeasurementPageView(id)) {
        gtag('event', 'page_view')
      }
    },
  })
}

export type GoogleCampaignTrackingOptions = {
  /** GA4 Measurement ID (G-…) — campo `gtm_measurement_id` da campanha */
  measurementId?: string | null
  /** GTM Container ID (GTM-…) — campo `g_pixel` da campanha */
  googleTagManagerContainerId?: string | null
}

/**
 * Inicia rastreamento Google na landing/widget.
 * Se GTM estiver configurado, carrega apenas o GTM (o GA costuma ir dentro do contêiner). Caso contrário, carrega gtag direto para o G-.
 */
export function initializeGoogleCampaignTracking(options: GoogleCampaignTrackingOptions): void {
  const container = normalizeGtmContainerId(options.googleTagManagerContainerId ?? null)
  const measurement = normalizeGa4MeasurementId(options.measurementId ?? null)

  if (container) {
    bootstrapGtmContainer(container)
    return
  }

  if (measurement) {
    bootstrapGtagMeasurement(measurement)
  }
}
